import React, {
  useMemo,
  useCallback,
  createContext,
  useContext,
  useState,
} from 'react';
import {
  Autocomplete,
  FormControl,
  Checkbox,
  TextField,
  Box,
  Paper,
  Divider,
  createFilterOptions,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import { useToggleModal } from '@hooks';

import { ButtonV2, IconButtonV2 } from 'components/common/ui';
import { MultiSelectDeleteModal } from './MultiSelectActionModals';

import styles from './multiSelectWithActions.style';

// Context to pass data to the stable PaperComponent
const MultiSelectContext = createContext({});

// Stable PaperComponent defined outside to prevent re-creation on every render
const CustomPaper = ({ children, ...paperProps }) => {
  const { value, options, hasSelectAllOption, onChange, handleOnChange } =
    useContext(MultiSelectContext);

  return (
    <Paper
      sx={styles.paper}
      onMouseDown={(e) => e.preventDefault()}
      {...paperProps}
    >
      {!hasSelectAllOption ? (
        <>
          {value?.length > 0 && (
            <>
              <Box
                onMouseDown={(e) => e.preventDefault()}
                sx={styles.headerWrapper}
              >
                <Box sx={{ flex: 1 }} />
                <ButtonV2
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOnChange(null, []);
                  }}
                  sx={styles.resetButton}
                >
                  Reset
                </ButtonV2>
              </Box>
              <Divider />
            </>
          )}
          {children}
        </>
      ) : (
        <>
          <Box
            onMouseDown={(e) => e.preventDefault()}
            sx={styles.headerWrapper}
          >
            <ButtonV2
              variant="text"
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(value?.length === options?.length ? [] : options);
              }}
              sx={styles.selectAllButton}
            >
              {value?.length === options?.length
                ? 'Deselect all'
                : 'Select all'}
            </ButtonV2>
            {value?.length > 0 && (
              <ButtonV2
                variant="text"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOnChange(null, []);
                }}
                sx={styles.resetButton}
              >
                Reset
              </ButtonV2>
            )}
          </Box>
          <Divider />
          {children}
        </>
      )}
    </Paper>
  );
};

const MultiSelectWithActions = ({
  value,
  onChange,
  type,
  loading = false,
  label = '',
  options,
  variant = 'outlined', // standard, outlined, filled
  error,
  name,
  minWidth,
  hasSelectAllOption = false,
  keepScrollPosition = false, // When true, dropdown won't scroll to top on selection
  freeSolo = true,
  isAllowedActions = true, // Controls whether onAdd and onDelete are available
  onAddNew,
  onDelete,
  ...props
}) => {
  const handleOnChange = useCallback(
    (_event, newValue, reason) => {
      if (onChange) {
        onChange(newValue, reason);
      }
    },
    [onChange]
  );

  const [selectedOptionToDelete, setSelectedOptionToDelete] = useState(null);
  const {
    isOpen: isDeleteOptionModalOpen,
    handleOpen: openDeleteOptionModal,
    handleClose: closeDeleteOptionModal,
  } = useToggleModal();

  const handleDeleteOption = useCallback(
    (optionToDelete) => {
      setSelectedOptionToDelete(optionToDelete);
      openDeleteOptionModal();
    },
    [openDeleteOptionModal]
  );

  const handleCloseDeleteOptionModal = useCallback(() => {
    closeDeleteOptionModal();
    setSelectedOptionToDelete(null);
  }, [closeDeleteOptionModal]);

  const handleConfirmDeleteOption = useCallback(() => {
    if (!selectedOptionToDelete) {
      return;
    }
    // Call the parent's onDelete handler with the option to delete
    onDelete?.(selectedOptionToDelete);
    handleCloseDeleteOptionModal();
  }, [selectedOptionToDelete, onDelete, handleCloseDeleteOptionModal]);

  const defaultFilter = useMemo(() => createFilterOptions(), []);

  const filterOptions = useCallback(
    (optionsToFilter, state) => {
      const filtered = defaultFilter(optionsToFilter, state);

      // If isDisabled is true the respective option will be filtered out
      // If isDisabled is undefined it won't affect the filtering
      const activeFiltered = filtered.filter((opt) => opt?.isDisabled !== true);

      if (
        freeSolo &&
        state.inputValue &&
        !activeFiltered.some(
          (opt) => opt.text?.toLowerCase() === state.inputValue.toLowerCase()
        )
      ) {
        activeFiltered.push({
          value: state.inputValue,
          text: state.inputValue,
          isUserInput: true,
        });
      }

      return activeFiltered;
    },
    [freeSolo, defaultFilter]
  );

  const icon = (
    <CheckBoxOutlineBlankIcon
      data-testid="CheckBoxOutlineBlankIcon"
      fontSize="small"
    />
  );

  const checkedIcon = (
    <CheckBoxIcon data-testid="CheckBoxIcon" fontSize="small" />
  );
  const hasValue = !!value?.length;

  const renderOption = (autoCompleteProps, option, { selected }) => {
    const { key, ...restAutoCompleteProps } = autoCompleteProps;

    // Check if this is a new user input option
    const isNewLabel = option.isUserInput;

    if (isNewLabel) {
      // Don't render add option if actions are not allowed
      if (!isAllowedActions) {
        return null;
      }

      const handleAddNew = (event) => {
        event.stopPropagation();
        onAddNew?.(option);
      };

      return (
        <Box
          component="li"
          key={`${option.value}-${key}`}
          {...restAutoCompleteProps}
          sx={styles.addNewTagTextWrapper}
          onClick={handleAddNew}
        >
          <Box sx={styles.optionItem}>
            <Box sx={styles.addNewTagLabel}>
              <AddIcon data-testid="AddIcon" sx={{ fontSize: 16 }} />
              <Box component="span" sx={styles.addNewTagText}>
                Add Tag
              </Box>
            </Box>
            {option.text || ''}
          </Box>
        </Box>
      );
    }

    const handleDeleteClick = (event) => {
      event.stopPropagation();
      handleDeleteOption(option);
    };

    return (
      <Box
        component="li"
        key={`${option.value}-${key}`}
        {...restAutoCompleteProps}
        sx={styles.actionItemWrapper}
      >
        <Box sx={styles.optionItem}>
          <Checkbox
            key={`checkbox-${key}`}
            size="small"
            icon={icon}
            checkedIcon={checkedIcon}
            sx={styles.checkbox}
            checked={selected}
          />
          {option.text || ''}
        </Box>
        {isAllowedActions && (
          <Box sx={styles.deleteIconWrapper}>
            <IconButtonV2
              size="small"
              variant="text"
              onClick={handleDeleteClick}
              title="Delete"
            >
              <DeleteIcon data-testid="DeleteIcon" sx={styles.deleteIcon} />
            </IconButtonV2>
          </Box>
        )}
      </Box>
    );
  };

  const renderInput = (params) => {
    return (
      <TextField
        variant="outlined"
        placeholder={hasValue ? '' : label}
        error={!!error}
        sx={{ ...styles.textfield(variant) }}
        data-testid={`input-${name}`}
        {...params}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <Box sx={styles.endAdornmentWrapper}>
              {params.InputProps?.endAdornment?.props?.children[0]}
              <ArrowDropDownIcon
                data-testid="ArrowDropDownIcon"
                color="dropdownArrow"
                sx={{
                  ...styles.icon,
                }}
              />
            </Box>
          ),
        }}
      />
    );
  };

  const contextValue = useMemo(
    () =>
      keepScrollPosition
        ? {
            value,
            options,
            hasSelectAllOption,
            onChange,
            handleOnChange,
          }
        : undefined,
    [
      keepScrollPosition,
      value,
      options,
      hasSelectAllOption,
      onChange,
      handleOnChange,
    ]
  );

  const autocomplete = (
    <Autocomplete
      multiple
      disablePortal={!keepScrollPosition}
      disableCloseOnSelect
      {...(keepScrollPosition && { disableListWrap: true })}
      limitTags={2}
      sx={styles.autocomplete}
      disabled={loading}
      value={value || []}
      onChange={handleOnChange}
      loading={loading}
      isOptionEqualToValue={(option, newValue) => {
        return option.value === newValue?.value;
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string' || option instanceof String) {
          return option;
        }
        return option?.text || option?.label;
      }}
      options={options}
      {...(keepScrollPosition && { PaperComponent: CustomPaper })}
      filterOptions={filterOptions}
      renderOption={renderOption}
      renderInput={renderInput}
      size="small"
      slotProps={{
        listbox: { style: { maxHeight: 300 } },
        ...(keepScrollPosition && {
          popper: { style: { zIndex: 1400 } },
        }),
      }}
      {...props}
    />
  );

  const formControl = (
    <FormControl variant={variant} sx={{ m: 0, width: '100%' }}>
      <Box sx={styles.wrapper(minWidth)}>{autocomplete}</Box>
    </FormControl>
  );

  if (keepScrollPosition) {
    return (
      <>
        <MultiSelectContext.Provider value={contextValue}>
          {formControl}
        </MultiSelectContext.Provider>
        {isAllowedActions && (
          <MultiSelectDeleteModal
            title="option"
            itemTitle={selectedOptionToDelete?.text}
            isOpen={isDeleteOptionModalOpen}
            onClose={handleCloseDeleteOptionModal}
            onConfirm={handleConfirmDeleteOption}
          />
        )}
      </>
    );
  }

  return (
    <>
      {formControl}
      {isAllowedActions && (
        <MultiSelectDeleteModal
          title="option"
          itemTitle={selectedOptionToDelete?.text}
          isOpen={isDeleteOptionModalOpen}
          onClose={handleCloseDeleteOptionModal}
          onConfirm={handleConfirmDeleteOption}
        />
      )}
    </>
  );
};

export default MultiSelectWithActions;
